/** Types for the derived secondary-connectivity props (Phase 3 UX).
 *  Mirror of src/topology_platform/schemas/connectivity.py — keep in sync.
 *
 *  CONSUMPTION CONTRACT (Phase 2 handoff): readers see the latest published run
 *  per feeder, with active manual overrides applied on top. The derived tables
 *  are EMPTY until a gated prod compute runs, so every field degrades to null /
 *  available:false — null `tier` means "no data / unknown", never an error. */

export type RootMethod =
  | 'manual_override'
  | 'meter_majority_strong'
  | 'meter_majority_weak'
  | 'node_id'
  | 'geom_snap'
  | 'unrooted'

export type ConnectivityTier = 'auto' | 'flagged' | 'review'

/** Per-component derived connectivity props carried on each `/map/secondary`
 *  feature when `include_connectivity=true` (flat `root_*` keys). All null when
 *  the feeder has no published run. */
export interface SecondaryConnectivityProps {
  root_transformer_id: number | null
  root_method: RootMethod | null
  root_confidence: number | null
  tier: ConnectivityTier | null
  transformer_reachable: boolean | null
}

/** The secondary leg of a meter's upstream trace (transformer -> secondary ->
 *  meter). `available:false` => fall back to the FK-only trace. Mirrors the
 *  `secondary` object on TraceResult and the standalone
 *  GET /secondary/meters/{id}/secondary-trace response. */
export interface SecondaryTracePath {
  available: boolean
  run_id: number | null
  component_id: number | null
  transformer_id: number | null
  attach_segment_id: number | null
  attach_distance_ft: number | null
  path_method: string | null
  confidence: number | null
  agrees_with_xfmr_id: boolean | null
  tier: ConnectivityTier | null
}

/** A transformer's connected secondary segments + meters (TransformerDetail).
 *  `available:false` + empty lists when no published run covers its feeder. */
export interface TransformerSecondaryDetail {
  transformer_id: number
  available: boolean
  run_id: number | null
  component_ids: number[]
  secondary_segment_ids: number[]
  meter_ids: number[]
  meter_count: number
  secondary_segment_count: number
}

/** Color-by-confidence palette keyed off the derived `tier`:
 *  auto = green, flagged = amber, review = red, no-data/unknown = neutral gray
 *  (plan Phase 3 step 14). UG remains dashed, OH solid in both modes. */
export const TIER_COLORS: Record<ConnectivityTier, string> = {
  auto: '#2ecc71',
  flagged: '#f1c40f',
  review: '#e74c3c',
}

/** Style color for a secondary feature's derived tier. `unrooted` method (which
 *  carries tier='review') and a missing tier both fall through to neutral gray
 *  so data gaps read as "unknown", not as a valid state. */
export const NO_CONNECTIVITY_COLOR = '#9ca3af'

export function tierColor(tier: ConnectivityTier | null | undefined): string {
  if (!tier) return NO_CONNECTIVITY_COLOR
  return TIER_COLORS[tier] ?? NO_CONNECTIVITY_COLOR
}
